import React, { useState, useEffect } from "react";
import BubbleChart from "./BubbleChart";
import { fetchCertificates, fetchKeys } from "services/apiServices";
import { CbomCertificate, CbomKeys } from "@schemas";
import KeysTable from "./KeysTable";
import { X, Filter } from "lucide-react";
import CertificatesTable from "./CertificatesTable";
import { useProjectContext } from "context/ProjectContext";

type GroupKey = string;

type AlgorithmGroup = {
  algorithm: string | undefined | null;
  keyLength: string | number | undefined | null;
  devices: Set<string>;
  keys: number;
  certificates: number;
  vulnerability: "Safe" | "Vulnerable" | "Unknown";
  certificatesSet?: Set<number>;
};

const ScanSpecificReports: React.FC = () => {
  const { state } = useProjectContext();
  const [allKeys, setAllKeys] = useState<CbomKeys[]>([]);
  const [allCertificates, setAllCertificates] = useState<CbomCertificate[]>([]);
  const [bubbleData, setBubbleData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedKeyData, setSelectedKeyData] = useState<CbomKeys[]>([]);
  const [selectedCertificateData, setSelectedCertificateData] = useState<
    CbomCertificate[]
  >([]);
  const [showModal, setShowModal] = useState({
    keys: false,
    certificates: false,
  });
  const [filter, setFilter] = useState({ algorithm: "", keyLength: "" });
  const [filterDropdown, setFilterDropdown] = useState({
    algorithm: false,
    keyLength: false,
  });

  const randomRGBA = () =>
    `rgba(${Math.floor(Math.random() * 256)}, ${Math.floor(
      Math.random() * 256
    )}, ${Math.floor(Math.random() * 256)}, 0.4)`;

  const processKeysAndCertificates = () => {
    try {
      // const keys = (await getCbomKeys()).data.data;

      // const certificates = (await getCbomCertificates()).data;
      // setAllCertificates(certificates);

      const algorithmCounts: Record<string, number> = {};

      // Keys: count key.algorithm
      allKeys.forEach((k: CbomKeys) => {
        const algo = k.algorithm;
        if (algo) {
          algorithmCounts[algo] = (algorithmCounts[algo] || 0) + 1;
        }
      });

      // Certificates: algo count
      allCertificates.forEach((cert: CbomCertificate) => {
        const uniqueAlgos = new Set([
          cert.public_key_algorithm,
          cert.original_signature_algorithm,
        ]);

        uniqueAlgos.forEach((algo) => {
          if (algo) algorithmCounts[algo] = (algorithmCounts[algo] || 0) + 1;
        });
      });

      console.log(algorithmCounts);

      const generatedAlgorithmData = (
        Object.entries(algorithmCounts) as [string, number][]
      ).map(([algorithm, count], index) => ({
        id: index + 1,
        name: `${algorithm}: ${count}`,
        fillColor: randomRGBA(),
        size: Math.max(30, count * 5),
        dYdX1: {
          dy: Math.floor(Math.random() * 7) - 3,
          dx: Math.floor(Math.random() * 7) - 3,
        },
      }));

      setBubbleData(generatedAlgorithmData);

      // const algorithmStats: Record<string, AlgorithmStats> = {};

      // // Populate from Keys
      // keys.forEach((k: CbomKeys) => {
      //   const algo = k.algorithm;
      //   if (!algo) return;
      //   if (!algorithmStats[algo]) {
      //     algorithmStats[algo] = {
      //       keyLengths: new Set(),
      //       devices: new Set(),
      //       keys: 0,
      //       certs: 0,
      //       vulnerability: k.quantum_safe_status || "Unknown",
      //     };
      //   }
      //   const stat = algorithmStats[algo];
      //   if (k.key_length) {
      //     stat.keyLengths.add(k.key_length);
      //   }
      //   if (k.device_id) {
      //     stat.devices.add(k.device_id);
      //   }
      //   stat.keys += 1;
      // });

      // // Populate from Certificates
      // certificates.forEach((c: CbomCertificate) => {
      //   const algos = [
      //     {
      //       name: c.public_key_algorithm,
      //       length: c.public_key_length,
      //       device: c.device_id,
      //       vuln: c.public_key_algorithm_qv,
      //     },
      //     { name: c.original_signature_algorithm, device: c.device_id },
      //     {
      //       name: c.signature_algorithm,
      //       vuln: c.signature_algorithm_qv,
      //       device: c.device_id,
      //     },
      //     { name: c.hashing_algorithm, device: c.device_id },
      //   ];

      //   algos.forEach(({ name, length, device, vuln }) => {
      //     if (!name) return;
      //     if (!algorithmStats[name]) {
      //       algorithmStats[name] = {
      //         keyLengths: new Set(),
      //         devices: new Set(),
      //         keys: 0,
      //         certs: 0,
      //         vulnerability: vuln || "Unknown",
      //       };
      //     }
      //     const stat = algorithmStats[name];
      //     if (length) stat.keyLengths.add(length);
      //     if (device) {
      //       stat.devices.add(device);
      //     }
      //     stat.certs += 1;
      //   });
      // });

      // // Generate Table Rows
      // const algorithmTableRows = Object.entries(algorithmStats).map(
      //   ([algorithm, stat], idx) => ({
      //     serial: idx + 1,
      //     algorithm,
      //     keyLength: Array.from(stat.keyLengths).join(", ") || "-",
      //     vulnerability: stat.vulnerability,
      //     deviceCount: stat.devices.size,
      //     keyCount: stat.keys,
      //     certCount: stat.certs,
      //   })
      // );

      const algorithmGroups: Record<GroupKey, AlgorithmGroup> = {};

      // Process keys
      allKeys.forEach((key: CbomKeys) => {
        const algo = key.algorithm;

        const groupKey = `${algo}_${key.key_length}`;
        if (!algorithmGroups[groupKey]) {
          algorithmGroups[groupKey] = {
            algorithm: algo,
            keyLength: key.key_length,
            devices: new Set(),
            keys: 0,
            certificates: 0,
            vulnerability:
              key.quantum_safe_status?.toLowerCase() === "vulnerable"
                ? "Vulnerable"
                : "Safe",
          };
        }
        algorithmGroups[groupKey].keys += 1;
        if (key.device_id) {
          algorithmGroups[groupKey].devices.add(key.device_id);
        }

        if (key.quantum_safe_status?.toLowerCase() === "vulnerable") {
          algorithmGroups[groupKey].vulnerability = "Vulnerable";
        }
      });

      // Process certificates and ensure no duplication in counting
      allCertificates.forEach((cert: CbomCertificate) => {
        const algs = [
          {
            name: cert.public_key_algorithm,
            length: cert.public_key_length,
            qv: cert.public_key_algorithm_qv,
          },
          // {
          //   name: cert.signature_algorithm,
          //   length: cert.public_key_length,
          //   qv: cert.signature_algorithm_qv,
          // },
          // {
          //   name: cert.hashing_algorithm,
          //   length: cert.public_key_length,
          //   qv: "Unknown",
          // },
          {
            name: cert.original_signature_algorithm,
            length: cert.public_key_length,
            qv: cert.signature_algorithm_qv,
          },
        ];

        algs.forEach(({ name, length, qv }) => {
          if (!name) return;

          const groupKey = `${name}_${length}`;

          // If this group doesn't exist, create it
          if (!algorithmGroups[groupKey]) {
            algorithmGroups[groupKey] = {
              algorithm: name,
              keyLength: length,
              devices: new Set(),
              keys: 0,
              certificates: 0,
              vulnerability:
                qv?.toLowerCase() === "vulnerable"
                  ? "Vulnerable"
                  : qv?.toLowerCase() === "unknown"
                  ? "Unknown"
                  : "Safe",
            };
          }

          // Ensure we're counting certificates only once for each algorithm+length combo
          if (!algorithmGroups[groupKey].certificatesSet) {
            algorithmGroups[groupKey].certificatesSet = new Set();
          }

          if (cert.c_id) {
            if (!algorithmGroups[groupKey].certificatesSet.has(cert.c_id)) {
              algorithmGroups[groupKey].certificates += 1;
              algorithmGroups[groupKey].certificatesSet.add(cert.c_id); // Mark as counted
            }
          }

          if (cert.device_id) {
            algorithmGroups[groupKey].devices.add(cert.device_id);
          }

          // Upgrade vulnerability if necessary
          if (qv?.toLowerCase() === "vulnerable") {
            algorithmGroups[groupKey].vulnerability = "Vulnerable";
          } else if (qv?.toLowerCase() === "unknown") {
            algorithmGroups[groupKey].vulnerability = "Unknown";
          } else if (qv?.toLowerCase() === "safe") {
            algorithmGroups[groupKey].vulnerability = "Safe";
          }
        });
      });

      // Process certificates and ensure no duplication in counting
      // certificates.forEach((cert: CbomCertificate) => {
      //   const algs = [
      //     {
      //       name: cert.public_key_algorithm,
      //       length: cert.public_key_length,
      //       qv: cert.public_key_algorithm_qv,
      //       groupByLength: true,
      //     },
      //     {
      //       name: cert.original_signature_algorithm,
      //       length: null,
      //       qv: cert.signature_algorithm_qv,
      //       groupByLength: false,
      //     },
      //   ];

      //   algs.forEach(({ name, length, qv, groupByLength }) => {
      //     if (!name) return;

      //     const groupKey = groupByLength ? `${name}_${length}` : `${name}_null`;

      //     if (!algorithmGroups[groupKey]) {
      //       algorithmGroups[groupKey] = {
      //         algorithm: name,
      //         keyLength: groupByLength ? length : null,
      //         devices: new Set(),
      //         keys: 0,
      //         certificates: 0,
      //         vulnerability:
      //           qv === "Safe"
      //             ? "Safe"
      //             : qv === "Vulnerable"
      //             ? "Vulnerable"
      //             : "Unknown",
      //       };
      //     }

      //     // Deduplicate certificate counts
      //     if (!algorithmGroups[groupKey].certificatesSet) {
      //       algorithmGroups[groupKey].certificatesSet = new Set();
      //     }

      //     if (
      //       cert.c_id &&
      //       !algorithmGroups[groupKey].certificatesSet.has(cert.c_id)
      //     ) {
      //       algorithmGroups[groupKey].certificates += 1;
      //       algorithmGroups[groupKey].certificatesSet.add(cert.c_id);
      //     }

      //     if (cert.device_id) {
      //       algorithmGroups[groupKey].devices.add(cert.device_id);
      //     }

      //     // Escalate to Vulnerable if needed
      //     if (qv === "Vulnerable") {
      //       algorithmGroups[groupKey].vulnerability = "Vulnerable";
      //     }
      //   });
      // });

      const tableData = Object.values(algorithmGroups).map((group, idx) => ({
        id: idx + 1,
        algorithm: group.algorithm,
        keyLength: group.keyLength,
        vulnerability: group.vulnerability,
        numDevices: group.devices.size,
        numKeys: group.keys,
        numCertificates: group.certificates,
      }));

      setTableData(tableData);
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    const fetchKeysAndCertificates = async () => {
      if (state.currentProjectID) {
        await fetchKeys(
          (data) => {
            setAllKeys(data);
          },
          "",
          false,
          state.currentProjectID
        );
        await fetchCertificates(
          (data) => {
            setAllCertificates(data);
          },
          "",
          false,
          state.currentProjectID
        );
      }
    };
    fetchKeysAndCertificates();
  }, []);

  useEffect(() => {
    if (allKeys.length && allCertificates.length) {
      processKeysAndCertificates();
    }
  }, [allKeys, allCertificates]);

  const uniqueAlgorithms = tableData
    .map((row) => row.algorithm)
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort();
  const uniqueKeyLengths = tableData
    .filter((row) =>
      filter.algorithm ? row.algorithm === filter.algorithm : true
    )
    .map((row) => row.keyLength)
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort((a, b) => (a ?? 0) - (b ?? 0));

  const filteredTableData = tableData.filter((d) => {
    return (
      (filter.algorithm ? d.algorithm === filter.algorithm : true) &&
      (filter.keyLength ? d.keyLength === filter.keyLength : true)
    );
  });

  return (
    <>
      <div className="bg-custom-blue8 rounded-lg px-[10px] py-[20px]">
        <div className="grid grid-cols-1 gap-4 w-full">
          <div>
            {bubbleData.length > 0 && (
              <BubbleChart title="Crypto Algorithms" bubbleData={bubbleData} />
            )}
          </div>
          <div className="mx-auto p-[10px] bg-white min-h-[380px] rounded-md shadow-md w-full">
            <div className="w-full overflow-auto custom-scrollbar rounded-lg border min-h-[358px] max-h-[358px]">
              <table
                id="data-table"
                className="min-w-full bg-white shadow rounded-lg"
              >
                <thead className="sticky top-0 z-10 bg-custom-blue0 h-[40px]">
                  <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                    <th className="p-2">S. No</th>
                    <th className="p-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            setFilterDropdown((prev) => ({
                              ...prev,
                              algorithm: !prev.algorithm,
                            }))
                          }
                        >
                          <Filter size={18} />
                        </button>
                        Algorithms
                      </div>
                      {filterDropdown.algorithm && (
                        <div className="absolute bg-white text-custom-blue6 border rounded shadow p-1 z-20">
                          <div
                            onClick={() => {
                              setFilter((prev) => ({ ...prev, algorithm: "" }));
                              setFilterDropdown((prev) => ({
                                ...prev,
                                algorithm: false,
                              }));
                            }}
                            className="cursor-pointer hover:bg-gray-100 px-2 py-1"
                          >
                            All
                          </div>
                          {uniqueAlgorithms.map((alg) => (
                            <div
                              key={alg}
                              onClick={() => {
                                setFilter((prev) => ({
                                  ...prev,
                                  algorithm: alg,
                                  keyLength: "",
                                }));
                                setFilterDropdown((prev) => ({
                                  ...prev,
                                  algorithm: false,
                                  keyLength: false,
                                }));
                              }}
                              className="cursor-pointer hover:bg-gray-100 px-2 py-1"
                            >
                              {alg}
                            </div>
                          ))}
                        </div>
                      )}
                    </th>
                    <th className="p-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            setFilterDropdown((prev) => ({
                              ...prev,
                              keyLength: !prev.keyLength,
                            }))
                          }
                        >
                          <Filter size={18} />
                        </button>
                        Key Length
                      </div>
                      {filterDropdown.keyLength && (
                        <div className="absolute bg-white text-black border rounded shadow p-1 z-20">
                          <div
                            onClick={() => {
                              setFilter((prev) => ({ ...prev, keyLength: "" }));
                              setFilterDropdown((prev) => ({
                                ...prev,
                                keyLength: false,
                              }));
                            }}
                            className="cursor-pointer hover:bg-gray-100 px-2 py-1"
                          >
                            All
                          </div>
                          {uniqueKeyLengths.map((len) => (
                            <div
                              key={len}
                              onClick={() => {
                                setFilter((prev) => ({
                                  ...prev,
                                  keyLength: len,
                                }));
                                setFilterDropdown((prev) => ({
                                  ...prev,
                                  keyLength: false,
                                }));
                              }}
                              className="cursor-pointer hover:bg-gray-100 px-2 py-1"
                            >
                              {len}
                            </div>
                          ))}
                        </div>
                      )}
                    </th>
                    <th className="p-2">No. of Keys</th>
                    <th className="p-2">No. of Certificates</th>
                    <th className="p-2">Vulnerability</th>
                    <th className="p-2">No. Of Devices</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTableData.length > 0 ? (
                    <>
                      {filteredTableData.map((data) => (
                        <tr
                          key={data.id}
                          className={`${
                            data.serial % 2 === 1 ? "bg-custom-blue0" : ""
                          } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                        >
                          <td className="p-2">{data.id}</td>
                          <td className="p-2">{data.algorithm}</td>
                          <td className="p-2">{data.keyLength}</td>
                          <td
                            className="p-2 text-custom-primaryBlueNew underline cursor-pointer"
                            onClick={() => {
                              const filteredKeys = allKeys.filter(
                                (k) =>
                                  k.algorithm === data.algorithm &&
                                  k.key_length === data.keyLength
                              );
                              setSelectedKeyData(filteredKeys);
                              setShowModal((prev) => ({ ...prev, keys: true }));
                            }}
                          >
                            {data.numKeys}
                          </td>
                          <td
                            className="p-2 text-custom-primaryBlueNew underline cursor-pointer"
                            onClick={() => {
                              const filteredCertificates =
                                allCertificates.filter(
                                  (c) =>
                                    (c.public_key_algorithm ===
                                      data.algorithm ||
                                      c.original_signature_algorithm ===
                                        data.algorithm) &&
                                    c.public_key_length === data.keyLength
                                );
                              setSelectedCertificateData(filteredCertificates);
                              setShowModal((prev) => ({
                                ...prev,
                                certificates: true,
                              }));
                            }}
                          >
                            {data.numCertificates}
                          </td>
                          <td className="p-2">{data.vulnerability}</td>
                          <td className="p-2">{data.numDevices}</td>
                        </tr>
                      ))}
                    </>
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center">
                        No Data Found!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {showModal.keys && (
        <div className="absolute inset-0 bg-black bg-opacity-20 h-full flex flex-col z-10 justify-center items-center">
          <div className="w-[80vw] relative">
            <button
              type="button"
              onClick={() => setShowModal((prev) => ({ ...prev, keys: false }))}
              className="absolute right-1 top-1 p-2 flex items-center justify-center rounded-full bg-custom-blue6"
            >
              <X color="#fff" size={15} />
            </button>
          </div>
          <div className="h-[60vh] w-[80vw]">
            <KeysTable customData={selectedKeyData} />
          </div>
        </div>
      )}
      {showModal.certificates && (
        <div className="absolute inset-0 bg-black bg-opacity-20 h-full flex flex-col z-10 justify-center items-center">
          <div className="w-[80vw] relative">
            <button
              type="button"
              onClick={() =>
                setShowModal((prev) => ({ ...prev, certificates: false }))
              }
              className="absolute right-1 top-1 p-2 flex items-center justify-center rounded-full bg-custom-blue6"
            >
              <X color="#fff" size={15} />
            </button>
          </div>
          <div className="h-[60vh] w-[80vw]">
            <CertificatesTable customData={selectedCertificateData} />
          </div>
        </div>
      )}
    </>
  );
};

export default ScanSpecificReports;
